import { useState, useEffect, useCallback } from "react";
import { useAuth, apiClient } from "@/App";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Loader2, Users, Mail, Settings as SettingsIcon, Copy, Check, Trash2,
  ShieldCheck, ShieldOff, Pencil, RefreshCw, Send, ExternalLink
} from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("users");

  if (authLoading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;
  }
  if (!user || user.role !== "superadmin") {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="admin-page">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-white tracking-tight">Administración</h1>
        <p className="text-slate-400 mt-1.5 text-sm">Gestión de usuarios, invitaciones y configuración global</p>
      </header>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-slate-900/50 border border-white/5 p-1 mb-6">
          <TabsTrigger value="users" className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white" data-testid="admin-tab-users">
            <Users className="w-4 h-4 mr-2" />
            Usuarios
          </TabsTrigger>
          <TabsTrigger value="invitations" className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white" data-testid="admin-tab-invitations">
            <Mail className="w-4 h-4 mr-2" />
            Invitaciones
          </TabsTrigger>
          <TabsTrigger value="config" className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white" data-testid="admin-tab-config">
            <SettingsIcon className="w-4 h-4 mr-2" />
            Configuración global
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users"><UsersPanel /></TabsContent>
        <TabsContent value="invitations"><InvitationsPanel /></TabsContent>
        <TabsContent value="config"><ConfigPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

// =================== USERS PANEL ===================
function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingQuota, setEditingQuota] = useState(null);
  const [quotaValue, setQuotaValue] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/admin/users");
      setUsers(res.data);
    } catch (e) {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setQuota = async (uid, value) => {
    try {
      await apiClient.put(`/admin/users/${uid}/quota`, { monthly_quota: parseInt(value) });
      toast.success("Cuota actualizada");
      setEditingQuota(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar cuota");
    }
  };

  const toggleStatus = async (uid, currentlyActive) => {
    const action = currentlyActive ? "desactivar" : "activar";
    if (!window.confirm(`¿Seguro que quieres ${action} este usuario?`)) return;
    try {
      await apiClient.put(`/admin/users/${uid}/status`, { is_active: !currentlyActive });
      toast.success(currentlyActive ? "Usuario desactivado" : "Usuario activado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar estado");
    }
  };
  
  const toggleRole = async (uid, currentRole, email) => {
    const promoting = currentRole !== "superadmin";
    const msg = promoting
      ? `¿Promover a ${email} a superadmin? Tendrá cuota ilimitada y acceso al panel de admin.`
      : `¿Degradar a ${email} a usuario normal? Su cuota volverá a 10/mes.`;
    if (!window.confirm(msg)) return;
    try {
      await apiClient.put(`/admin/users/${uid}/role`, { role: promoting ? "superadmin" : "user" });
      toast.success(promoting ? "Usuario promovido a superadmin" : "Usuario degradado a usuario");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar rol");
    }
  };

  const resetQuota = async (uid) => {
    if (!window.confirm("¿Reiniciar la cuota usada de este usuario a 0?")) return;
    try {
      await apiClient.post(`/admin/users/${uid}/reset-quota`);
      toast.success("Cuota reiniciada");
      load();
    } catch (e) {
      toast.error("Error al reiniciar cuota");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;
  }

  return (
    <div className="glass rounded-xl overflow-hidden">
      <table className="w-full text-sm" data-testid="admin-users-table">
        <thead>
          <tr className="border-b border-white/5 bg-slate-900/40">
            <th className="text-left p-4 text-slate-300 font-medium">Usuario</th>
            <th className="text-left p-4 text-slate-300 font-medium">Rol</th>
            <th className="text-left p-4 text-slate-300 font-medium">Registro</th>
            <th className="text-left p-4 text-slate-300 font-medium">Último acceso</th>
            <th className="text-left p-4 text-slate-300 font-medium">Análisis usados</th>
            <th className="text-left p-4 text-slate-300 font-medium">Cuota</th>
            <th className="text-left p-4 text-slate-300 font-medium">Estado</th>
            <th className="text-right p-4 text-slate-300 font-medium">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const isUnlimited = u.quota?.unlimited;
            const isSuperadmin = u.role === "superadmin";
            return (
              <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={u.picture} />
                      <AvatarFallback className="bg-cyan-500/10 text-cyan-400 text-xs">
                        {(u.name || u.email).charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div className="text-white font-medium truncate">{u.name || "—"}</div>
                      <div className="text-xs text-slate-500 truncate">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  {isSuperadmin ? (
                    <button
                      onClick={() => !u.is_hardcoded_superadmin && toggleRole(u.id, u.role, u.email)}
                      disabled={u.is_hardcoded_superadmin}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-500/10 text-amber-400 text-xs font-medium ${u.is_hardcoded_superadmin ? 'cursor-not-allowed' : 'hover:bg-amber-500/20 cursor-pointer'}`}
                      title={u.is_hardcoded_superadmin ? "Superadmin permanente (no modificable)" : "Click para degradar a usuario"}
                      data-testid={`role-badge-${u.id}`}
                    >
                      <ShieldCheck className="w-3 h-3" /> Superadmin{u.is_hardcoded_superadmin && " 🔒"}
                    </button>
                  ) : (
                    <button
                      onClick={() => toggleRole(u.id, u.role, u.email)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-700/30 text-slate-300 text-xs hover:bg-slate-700/50 cursor-pointer"
                      title="Click para promover a superadmin"
                      data-testid={`role-badge-${u.id}`}
                    >
                      Usuario
                    </button>
                  )}
                </td>
                <td className="p-4 text-xs text-slate-400">{formatDate(u.created_at)}</td>
                <td className="p-4 text-xs text-slate-400">{formatDate(u.last_login_at) || "—"}</td>
                <td className="p-4">
                  <div className="text-white">{u.quota?.used ?? 0}</div>
                  <div className="text-xs text-slate-500">total: {u.analyses_total}</div>
                </td>
                <td className="p-4">
                  {editingQuota === u.id ? (
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        value={quotaValue}
                        onChange={(e) => setQuotaValue(e.target.value)}
                        className="w-20 h-8 bg-slate-950/50 border-white/10 text-white text-xs"
                        autoFocus
                      />
                      <Button size="sm" onClick={() => setQuota(u.id, quotaValue)} className="h-8 px-2 bg-emerald-600 hover:bg-emerald-500">
                        <Check className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setEditingQuota(u.id); setQuotaValue(String(u.quota?.total ?? 10)); }}
                      className="inline-flex items-center gap-1 text-cyan-400 hover:text-cyan-300 text-sm"
                      data-testid={`edit-quota-${u.id}`}
                    >
                      {isUnlimited ? "Ilimitada" : `${u.quota?.total}/mes`}
                      <Pencil className="w-3 h-3 opacity-50" />
                    </button>
                  )}
                </td>
                <td className="p-4">
                  {u.is_active ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-xs">
                      Activo
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 text-xs">
                      Suspendido
                    </span>
                  )}
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => resetQuota(u.id)}
                      className="h-8 px-2 text-slate-400 hover:text-cyan-400"
                      title="Reiniciar cuota usada a 0"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </Button>
                    {!isSuperadmin && (
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => toggleStatus(u.id, u.is_active)}
                        className={`h-8 px-2 ${u.is_active ? "text-red-400 hover:text-red-300" : "text-emerald-400 hover:text-emerald-300"}`}
                        data-testid={`toggle-status-${u.id}`}
                      >
                        <ShieldOff className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {users.length === 0 && (
        <div className="p-12 text-center text-slate-500">Aún no hay usuarios registrados</div>
      )}
    </div>
  );
}

// =================== INVITATIONS PANEL ===================
function InvitationsPanel() {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/admin/invitations");
      setInvitations(res.data);
    } catch (e) {
      toast.error("Error al cargar invitaciones");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setCreating(true);
    try {
      const res = await apiClient.post("/admin/invitations", { email: email.trim() });
      if (res.data.email_sent) {
        toast.success(`Invitación enviada a ${res.data.email}`);
      } else {
        toast.warning(`Invitación creada pero email no enviado. Comparte el link manualmente.`);
      }
      setEmail("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear invitación");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Borrar esta invitación?")) return;
    try {
      await apiClient.delete(`/admin/invitations/${id}`);
      toast.success("Invitación borrada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al borrar");
    }
  };

  const copyLink = async (id, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div className="space-y-6">
      {/* Form */}
      <div className="glass rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-1">Nueva invitación</h3>
        <p className="text-sm text-slate-400 mb-4">El invitado recibirá un email con un enlace único de registro.</p>
        <form onSubmit={create} className="flex gap-3">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@ejemplo.com"
            className="flex-1 bg-slate-950/50 border-white/10 text-white"
            required
            data-testid="invite-email-input"
          />
          <Button type="submit" disabled={creating} className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="create-invite-btn">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Crear Invitación
          </Button>
        </form>
      </div>

      {/* List */}
      <div className="glass rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-invitations-table">
            <thead>
              <tr className="border-b border-white/5 bg-slate-900/40">
                <th className="text-left p-4 text-slate-300 font-medium">Email</th>
                <th className="text-left p-4 text-slate-300 font-medium">Creada</th>
                <th className="text-left p-4 text-slate-300 font-medium">Estado</th>
                <th className="text-right p-4 text-slate-300 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {invitations.map((inv) => (
                <tr key={inv.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-4 text-white">{inv.email}</td>
                  <td className="p-4 text-xs text-slate-400">{formatDate(inv.created_at)}</td>
                  <td className="p-4">
                    {inv.used_at ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-xs">
                        Utilizada · {formatDate(inv.used_at)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 text-xs">
                        Pendiente
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => copyLink(inv.id, inv.invite_url)}
                        className="h-8 px-2 text-slate-400 hover:text-cyan-400"
                        title="Copiar enlace"
                        data-testid={`copy-invite-${inv.id}`}
                      >
                        {copiedId === inv.id ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      </Button>
                      <a href={inv.invite_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center h-8 px-2 text-slate-400 hover:text-cyan-400" title="Abrir enlace">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                      {!inv.used_at && (
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => remove(inv.id)}
                          className="h-8 px-2 text-red-400 hover:text-red-300"
                          data-testid={`delete-invite-${inv.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && invitations.length === 0 && (
          <div className="p-12 text-center text-slate-500">No hay invitaciones todavía</div>
        )}
      </div>
    </div>
  );
}

// =================== CONFIG PANEL ===================
function ConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [adsLimit, setAdsLimit] = useState(10);
  const [savingLimit, setSavingLimit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/admin/config");
      setCfg(res.data);
      setAdsLimit(res.data.ads_limit_per_competitor ?? 10);
    } catch (e) {
      toast.error("Error al cargar la configuración");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put("/admin/config", { apify_api_key: newKey.trim() });
      toast.success("API Key de Apify actualizada");
      setEditing(false);
      setNewKey("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const saveAdsLimit = async () => {
    setSavingLimit(true);
    try {
      await apiClient.put("/admin/config", { ads_limit_per_competitor: adsLimit });
      toast.success("Límite de anuncios actualizado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar el límite");
    } finally {
      setSavingLimit(false);
    }
  };

  if (loading || !cfg) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;
  }

  const minLimit = cfg.ads_limit_min ?? 5;
  const maxLimit = cfg.ads_limit_max ?? 20;
  const limitDirty = adsLimit !== (cfg.ads_limit_per_competitor ?? 10);

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Apify Key card */}
      <div className="glass rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-2">API Key de Apify (global)</h3>
        <p className="text-sm text-slate-400 mb-6">
          Esta API key se usa para todos los usuarios. Solo el superadmin puede modificarla.
        </p>

        <div className="space-y-4">
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">Estado</Label>
            <div className="mt-1.5 flex items-center gap-2">
              {cfg.apify_api_key_set ? (
                <>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-xs">
                    <Check className="w-3 h-3" /> Configurada
                  </span>
                  <span className="text-xs text-slate-500">Origen: {cfg.source === "admin_ui" ? "UI Admin" : "Variable de entorno"}</span>
                </>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 text-xs">
                  Sin configurar
                </span>
              )}
            </div>
          </div>

          {cfg.apify_api_key_set && !editing && (
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Valor actual (enmascarado)</Label>
              <div className="mt-1.5 font-mono text-sm text-slate-300 bg-slate-950/50 border border-white/10 rounded-lg px-3 py-2">
                {cfg.apify_api_key_masked || "—"}
              </div>
            </div>
          )}

          {editing ? (
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Nueva API Key</Label>
              <Input
                type="password"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder="apify_api_..."
                className="mt-1.5 bg-slate-950/50 border-white/10 text-white font-mono text-sm"
                autoFocus
                data-testid="apify-key-input"
              />
              <div className="flex gap-2 mt-3">
                <Button onClick={save} disabled={saving || !newKey.trim()} className="bg-cyan-600 hover:bg-cyan-500" data-testid="save-apify-key-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Guardar
                </Button>
                <Button variant="outline" onClick={() => { setEditing(false); setNewKey(""); }} className="border-white/10 text-slate-300">
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <Button onClick={() => setEditing(true)} variant="outline" className="border-white/10 text-slate-200 hover:bg-white/5" data-testid="edit-apify-key-btn">
              <Pencil className="w-4 h-4 mr-2" />
              {cfg.apify_api_key_set ? "Cambiar API Key" : "Configurar API Key"}
            </Button>
          )}
        </div>
      </div>

      {/* Ads Limit card */}
      <div className="glass rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-2">Límite de anuncios por competidor (global)</h3>
        <p className="text-sm text-slate-400 mb-6">
          Máximo de anuncios que el sistema raspará por cada competidor en cada plataforma. Se priorizan los más activos, con más variantes y mayor alcance. Aplica a TODOS los usuarios por igual.
        </p>
        <div className="flex items-center gap-4">
          <Input
            type="number"
            min={minLimit}
            max={maxLimit}
            value={adsLimit}
            onChange={(e) => {
              const v = parseInt(e.target.value);
              if (Number.isNaN(v)) { setAdsLimit(10); return; }
              setAdsLimit(Math.max(minLimit, Math.min(maxLimit, v)));
            }}
            className="w-24 bg-slate-950/50 border-white/10 text-white text-center focus:border-cyan-500/50"
            data-testid="admin-ads-limit-input"
          />
          <span className="text-sm text-slate-400">anuncios (mín. {minLimit} — máx. {maxLimit})</span>
          {limitDirty && (
            <Button onClick={saveAdsLimit} disabled={savingLimit} className="bg-cyan-600 hover:bg-cyan-500" data-testid="save-ads-limit-btn">
              {savingLimit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
}
